import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { authenticatedFetch } from '../../utils/api';
import styles from './TongueTwisters.module.css';

const TongueTwisters = () => {
    const [twisters, setTwisters] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [practiceMode, setPracticeMode] = useState(false);
    const [activeTwisterIndex, setActiveTwisterIndex] = useState(0);
    const [attempts, setAttempts] = useState(0);
    const [nextAutoFetchTime, setNextAutoFetchTime] = useState(null);

    const fetchTongueTwisters = async () => {
        const cachedTwisters = localStorage.getItem('dailyTongueTwisters');
        const cachedNextAutoFetchTime = localStorage.getItem('nextTongueTwistersTime');

        if (cachedTwisters && cachedNextAutoFetchTime) {
            const parsedTwisters = JSON.parse(cachedTwisters);
            if (new Date() < new Date(cachedNextAutoFetchTime)) {
                setTwisters(parsedTwisters);
                setNextAutoFetchTime(cachedNextAutoFetchTime);
                setLoading(false);
                return;
            }
        }

        try {
            setLoading(true);
            const data = await authenticatedFetch('/api/tongue-twisters/daily');
            setTwisters(data.twisters);
            const nextRefreshTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
            setNextAutoFetchTime(nextRefreshTime);
            setError(null);
        } catch (err) {
            setError('Unable to retrieve tongue twisters');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = async () => {
        try {
            setRefreshing(true);
            const data = await authenticatedFetch('/api/tongue-twisters/refresh', {
                method: 'POST'
            });
            const nextRefreshTime = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
            setTwisters(data.twisters);
            setNextAutoFetchTime(nextRefreshTime);
            setError(null);
            setActiveTwisterIndex(0);
            setAttempts(0);
        } catch (err) {
            setError('Tongue twisters refresh failed');
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (twisters && nextAutoFetchTime) {
            localStorage.setItem('dailyTongueTwisters', JSON.stringify(twisters));
            localStorage.setItem('nextTongueTwistersTime', nextAutoFetchTime);
        }
    }, [twisters, nextAutoFetchTime]);

    const startPractice = () => {
        setPracticeMode(true);
        setAttempts(0);
    };

    const resetPractice = () => {
        setAttempts(0);
        setActiveTwisterIndex(0);
        setPracticeMode(false);
    };

    const nextTwister = () => {
        if (activeTwisterIndex < twisters.length - 1) {
            setActiveTwisterIndex(prev => prev + 1);
            setAttempts(0);
        } else {
            resetPractice();
        }
    };

    const incrementAttempts = () => {
        setAttempts(prev => prev + 1);
    };

    useEffect(() => {
        fetchTongueTwisters();
    }, []);

    if (loading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>
                    <div className={styles.loadingIndicator} />
                    <p className={styles.loadingMessage}>Loading tongue twisters...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>Daily Tongue Twisters</h2>
                    <div className={styles.sectionActions}>
                        <button
                            className={styles.actionButton}
                            onClick={handleRefresh}
                            disabled={refreshing}
                            aria-label="Refresh tongue twisters"
                        >
                            <RefreshCw
                                size={18}
                                className={`${styles.actionIcon} ${refreshing ? styles.spinning : ''}`}
                            />
                        </button>
                    </div>
                </div>

                {error && (
                    <div className={styles.errorBanner}>
                        <p className={styles.errorMessage}>{error}</p>
                    </div>
                )}

                {twisters && !practiceMode && (
                    <div className={styles.introCard}>
                        <p className={styles.introText}>
                            Today's tongue twisters are here to challenge your pronunciation skills!
                            These tricky phrases will help you improve your speech clarity and speed.
                        </p>
                        <button
                            className={styles.primaryButton}
                            onClick={startPractice}
                        >
                            Start Practice
                        </button>
                    </div>
                )}

                {twisters && practiceMode && (
                    <div className={styles.practiceCard}>
                        <h3 className={styles.practiceTitle}>
                            Tongue Twister {activeTwisterIndex + 1}
                        </h3>
                        <blockquote className={styles.twisterText}>
                            {twisters[activeTwisterIndex].text}
                        </blockquote>
                        <div className={styles.instructionsCard}>
                            <p className={styles.instructionsTitle}>Practice Goal:</p>
                            <ul className={styles.instructionsList}>
                                <li>Start slow and focus on clarity</li>
                                <li>Gradually increase speed as you improve</li>
                                <li>Repeat 10 times without stumbling</li>
                            </ul>
                        </div>
                        <div className={styles.progressTracker}>
                            <p className={styles.progressText}>Attempts: {attempts}/10</p>
                            <button
                                className={styles.progressButton}
                                onClick={incrementAttempts}
                                disabled={attempts >= 10}
                            >
                                {attempts < 10 ? 'Mark Attempt' : 'Goal Reached!'}
                            </button>
                        </div>
                        {attempts >= 10 && (
                            <button
                                className={styles.secondaryButton}
                                onClick={nextTwister}
                            >
                                {activeTwisterIndex < twisters.length - 1
                                    ? 'Next Tongue Twister'
                                    : 'Finish Practice'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TongueTwisters;